import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function PlatformTutorial() {
  const { token, refreshAccessToken } = useToken();
  const navigate = useNavigate();

  useProgressRedirect('PLATFORM_TUTORIAL');

  const [threatModelProvided, setThreatModelProvided] = useState(false);
  
  const checkThreatModel = async () => {
    try {
      const authToken = token || await refreshAccessToken();
      const resp = await fetch('/api' + '/accounts/threat-model', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await resp.json();
      return data.threat_model_provided ?? false;
    } catch (err) {
      console.error('Failed to check threat model', err);
      return false;
    }
  };

  const handleContinue = async () => {
    try {
      const authToken = token || await refreshAccessToken();

      const progressRes = await fetch('/api' + '/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'EXPERIMENT' }),
      });

      if (progressRes.ok) {
        navigate('/experiment', { replace: true });
      }
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };

  useEffect(() => {
    (async () => {
      const hasThreatModel = await checkThreatModel();
      setThreatModelProvided(hasThreatModel);
    })();
  }, []);

  return (
    <Container className="py-5">
      <ProgressTracker currentStep="platform_tutorial" />

      <Card className="shadow-lg border-0">
        <Card.Body>
          <h2 className="mb-3">🛠️ Platform Tutorial</h2>
          <p>
            This short walkthrough shows how to use the experiment platform: open design documents, start/restart servers, and submit findings.
          </p>

          {/* 1. Platform Overview */}
          <h5 className="mt-4">1. Platform Overview</h5>
          <Row className="mb-4">
            <Col md={12}>
              <div className="border p-3 bg-light">
                <img 
                  src={threatModelProvided ? "/images/platform_overview_withdocu.png" : "/images/platform_overview_nodocu.png"}
                  alt="Platform Overview" 
                  style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>

          {/* 2. Submit Findings */}
          <h5 className="mt-4">2. Submit Findings</h5>
          <Row className="mb-4">
            <Col md={12}>
              <div className="border p-3 bg-light">
                <img 
                  src={threatModelProvided ? "/images/submit_finding_withdocu.png" : "/images/submit_finding_nodocu.png"}
                  alt="Submit Findings" 
                  style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                />
              </div>
            </Col>
          </Row>

          {/* 3. Threat Model View Tutorial (only if threat model is provided) */}
          {threatModelProvided && (
            <>
              <h5 className="mt-4">3. Threat Model View Tutorial</h5>
              <Row className="mb-4">
                <Col md={12}>
                  <div className="border p-3 bg-light mb-3">
                    <img 
                      src="/images/tm_tutorial_1.png"
                      alt="Threat Model Tutorial 1" 
                      style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                    />
                  </div>
                  <div className="border p-3 bg-light">
                    <img 
                      src="/images/tm_tutorial_2.png"
                      alt="Threat Model Tutorial 2" 
                      style={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} 
                    />
                  </div>
                </Col>
              </Row>
            </>
          )}

          <div className="d-grid mt-4">
            <Button variant="success" size="lg" onClick={handleContinue}>Proceed to Targets</Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default PlatformTutorial;
